import { useState, useEffect, useCallback, useRef, memo, lazy, Suspense, useMemo } from "react";
import { SearchIcon as Search, ShoppingBagIcon as ShoppingBag, HeartIcon as Heart, MenuIcon as Menu, XIcon as X, UserIcon as User, ChevronDownIcon as ChevronDown, LogOutIcon as LogOut, PackageIcon as Package } from "./icons";
import { Home } from "lucide-react";
const getSupabase = () => import("@/integrations/supabase/client").then(m => m.supabase);
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchProductsForPath } from "@/lib/prefetchProducts";
import { useMenuCategories } from "@/hooks/useCategories";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import defaultLogo from "@/assets/logo.webp";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { navigateToStorefrontHome, scrollToStorefrontTarget, buildStorefrontHomePath } from "@/lib/storefrontNavigation";
const SearchModal = lazy(() => import("./SearchModal"));

const hexToHslHeader = (hex: string): string | null => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const blackHeaderStyles: React.CSSProperties = {
  backgroundColor: "hsl(25 40% 32%)",
  color: "hsl(0 0% 100%)",
  "--background": "25 40% 32%",
  "--foreground": "0 0% 100%",
  "--primary": "0 0% 100%",
  "--primary-foreground": "25 40% 32%",
  "--secondary": "25 40% 26%",
  "--secondary-foreground": "0 0% 100%",
  "--muted": "25 40% 26%",
  "--muted-foreground": "0 0% 100%",
  "--accent": "0 0% 100%",
  "--accent-foreground": "25 40% 32%",
  "--border": "25 30% 42%",
  "--input": "25 30% 42%",
  "--ring": "0 0% 100%",
  "--card": "0 0% 5%",
  "--card-foreground": "0 0% 100%",
  "--popover": "0 0% 5%",
  "--popover-foreground": "0 0% 100%",
} as any;

const Header = memo(() => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState<any>(null);
  const { isLightDesign } = useSiteTheme();
  const { content: siteSettings, loading: settingsLoading } = useSiteContent();
  const logoUrl = siteSettings.logo_url?.trim() || (!settingsLoading ? defaultLogo : "");
  const logoReady = !settingsLoading && Boolean(logoUrl);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const shouldLoadMenuCategories = catDropdownOpen || mobileOpen;
  const { data: categories = [] } = useMenuCategories(shouldLoadMenuCategories);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { openCart, closeCart, totalItems } = useCart();
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const handlePrefetch = useCallback((target: string) => {
    prefetchProductsForPath(`/${target}`, queryClient);
  }, [queryClient]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let cleanupAuth = () => {};
    let initialized = false;
    const setupAuth = async () => {
      if (initialized) return;
      initialized = true;
      const supabase = await getSupabase();
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
      });
      supabase.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null);
      });
      cleanupAuth = () => subscription.unsubscribe();
    };
    const interactionEvents: Array<keyof WindowEventMap> = ["pointerdown", "touchstart", "keydown", "scroll"];
    const onInteract = () => {
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, onInteract));
      setupAuth();
    };
    interactionEvents.forEach((eventName) => window.addEventListener(eventName, onInteract, { once: true, passive: true }));
    if (location.pathname !== "/") {
      if ("requestIdleCallback" in window) {
        const idleId = requestIdleCallback(() => onInteract(), { timeout: 1000 });
        return () => { cancelIdleCallback(idleId); interactionEvents.forEach((eventName) => window.removeEventListener(eventName, onInteract)); cleanupAuth(); };
      }
      onInteract();
      return () => { cleanupAuth(); };
    }
    return () => { interactionEvents.forEach((eventName) => window.removeEventListener(eventName, onInteract)); cleanupAuth(); };
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
      if (mobileOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node) && mobileToggleRef.current && !mobileToggleRef.current.contains(e.target as Node)) setMobileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  const showReviews = siteSettings.show_reviews_in_header === "true";

  const navLinks = [
    { label: t("nav_home"), target: "home", key: "home" },
    { label: t("nav_shop"), target: "products", isPage: true, key: "shop" },
    { label: t("nav_categories"), target: "categories", isDropdown: true, key: "categories" },
    { label: t("nav_best_selling"), target: "best-selling", isPage: true, key: "best-selling" },
    { label: t("nav_new_arrival", "New Arrival"), target: "new-arrivals", isPage: true, key: "new-arrivals" },
    { label: t("all_products", "All Products"), target: "products", isPage: true, key: "all-products" },
    ...(showReviews ? [{ label: t("nav_reviews", "Reviews"), target: "reviews", isPage: true, key: "reviews" }] : []),
    { label: t("nav_contact"), target: "contact", key: "contact" },
  ];

  const scrollToSection = useCallback((target: string) => {
    if (target === "home") {
      setMobileOpen(false); closeCart();
      if (location.pathname === "/") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
      navigateToStorefrontHome(navigate, location.search, { scrollToTop: true }); return;
    }
    setMobileOpen(false); closeCart();
    if (target === "contact") {
      if (location.pathname !== "/") { navigateToStorefrontHome(navigate, location.search, { scrollTarget: "contact" }); }
      else {
        const footer = document.getElementById("contact");
        if (footer) { footer.scrollIntoView({ behavior: "smooth", block: "start" }); }
        else { window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); setTimeout(() => { const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 800); }
      }
      return;
    }
    if (location.pathname !== "/") { navigateToStorefrontHome(navigate, location.search, { scrollTarget: target }); }
    else { scrollToStorefrontTarget(target); }
  }, [location.pathname, location.search, navigate, closeCart]);

  const handleCategoryClick = useCallback((slug: string) => {
    setCatDropdownOpen(false); setMobileCatOpen(false); setMobileOpen(false); navigate(`/category/${slug}`);
  }, [navigate]);

  const customHeaderStyles = useMemo((): React.CSSProperties => blackHeaderStyles, []);

  return (
    <>
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl shadow-lg border-b border-primary/10"
          : "backdrop-blur-sm"
      }`}
      style={customHeaderStyles}
    >
      {/* Two-row layout: Top row = logo + actions, Bottom row = nav */}
      <div className="container mx-auto px-4 lg:px-10">
        {/* Top row */}
        <div className="flex items-center justify-between h-[50px] md:h-[58px]">
          {/* Mobile menu toggle */}
          <button
            ref={mobileToggleRef}
            aria-label="Menu"
            className="lg:hidden p-2 rounded-lg text-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>

          {/* Left icons - desktop */}
          <div className="hidden lg:flex items-center gap-1">
            <button
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-lg text-foreground/70 hover:text-primary hover:bg-primary/10 transition-all duration-300"
            >
              <Search size={18} strokeWidth={1.5} />
            </button>
            <button
              aria-label="Wishlist"
              onClick={() => navigate("/wishlist")}
              className="p-2 rounded-lg text-foreground/70 hover:text-primary hover:bg-primary/10 transition-all duration-300"
            >
              <Heart size={18} strokeWidth={1.5} />
            </button>
          </div>

          {/* Center logo */}
          <a
            href={buildStorefrontHomePath(location.search)}
            className="absolute left-1/2 -translate-x-1/2 group"
            onClick={(e) => { e.preventDefault(); scrollToSection("home"); }}
          >
            {logoUrl ? (
              <img
                src={(() => {
                  const m = logoUrl.match(/^(https:\/\/[^/]+\.supabase\.co)\/storage\/v1\/object\/public\/(.+)$/);
                  if (!m) return logoUrl;
                  return `${m[1]}/storage/v1/render/image/public/${m[2]}?width=180&height=70&quality=75&resize=cover&format=origin`;
                })()}
                alt="Fatema Farista"
                width={120}
                height={44}
                className={`h-9 md:h-11 w-auto transition-all duration-500 group-hover:scale-105 ${logoReady ? "opacity-100" : "opacity-0"}`}
              />
            ) : (
              <div className="h-9 md:h-11 w-28" aria-hidden="true" />
            )}
          </a>

          {/* Right icons */}
          <div className="flex items-center gap-0.5">
            <button
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
              className="lg:hidden p-2 rounded-lg text-foreground/70 hover:text-primary hover:bg-primary/10 transition-all duration-300"
            >
              <Search size={18} strokeWidth={1.5} />
            </button>
            <button
              aria-label="Cart"
              className="p-2 rounded-lg text-foreground/70 hover:text-primary hover:bg-primary/10 transition-all duration-300 relative"
              onClick={openCart}
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span className="absolute top-0.5 right-0 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center font-body font-bold animate-fade-in">
                  {totalItems}
                </span>
              )}
            </button>
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => { if (!user) { navigate("/auth"); return; } setUserMenuOpen(!userMenuOpen); }}
                className="p-2 rounded-lg text-foreground/70 hover:text-primary hover:bg-primary/10 transition-all duration-300"
                aria-label={user ? "Profile" : "Login"}
              >
                <User size={18} strokeWidth={1.5} />
              </button>
              {userMenuOpen && user && (
                <div className="absolute right-0 top-full mt-2 min-w-[180px] bg-background border border-border rounded-xl shadow-2xl z-[60] py-2 animate-fade-in">
                  <button onClick={() => { setUserMenuOpen(false); navigate("/profile"); }} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-primary/5 transition-colors flex items-center gap-2.5">
                    <User size={14} strokeWidth={1.5} />
                    <span className="tracking-wider text-xs uppercase">{t("my_account")}</span>
                  </button>
                  <button onClick={() => { setUserMenuOpen(false); navigate("/profile?tab=orders"); }} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-primary/5 transition-colors flex items-center gap-2.5">
                    <Package size={14} strokeWidth={1.5} />
                    <span className="tracking-wider text-xs uppercase">{t("orders")}</span>
                  </button>
                  <div className="h-px bg-border mx-3 my-1" />
                  <button onClick={async () => { setUserMenuOpen(false); const sb = await getSupabase(); await sb.auth.signOut(); navigate("/"); }} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-primary/5 transition-colors flex items-center gap-2.5">
                    <LogOut size={14} strokeWidth={1.5} />
                    <span className="tracking-wider text-xs uppercase">{t("logout")}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop nav row — centered, separated */}
        <nav className="hidden lg:flex items-center justify-center gap-0 border-t border-primary/5 -mx-4 lg:-mx-10 px-4 lg:px-10">
          {navLinks.map((link) => {
            if ((link as any).isDropdown) {
              return (
                <div key={link.key} className="relative" onMouseEnter={() => setCatDropdownOpen(true)} onMouseLeave={() => setCatDropdownOpen(false)}>
                  <button
                    onClick={() => setCatDropdownOpen(!catDropdownOpen)}
                    className="px-5 py-2.5 text-[10.5px] font-body font-semibold tracking-[0.25em] uppercase text-foreground/80 hover:text-primary transition-all duration-300 bg-transparent border-none cursor-pointer flex items-center gap-1 relative after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-primary after:rounded-full after:transition-all after:duration-300 hover:after:w-2/3"
                  >
                    {link.label}
                    <ChevronDown size={11} className={`transition-transform duration-200 ${catDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {catDropdownOpen && (
                    <div className="absolute top-full left-0 mt-0 min-w-[240px] bg-background border border-border rounded-xl shadow-2xl z-[60] py-2 animate-fade-in">
                      {categories.length === 0 ? (
                        <p className="px-4 py-2 text-xs text-muted-foreground font-body">No categories</p>
                      ) : (
                        categories.map((cat) => (
                          <button key={cat.id} onClick={() => handleCategoryClick(cat.slug)} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-200 flex items-center gap-3">
                            {cat.image && <img src={cat.image} alt={cat.name} width={28} height={28} className="w-7 h-7 rounded-lg object-cover flex-shrink-0" loading="lazy" />}
                            <span className="tracking-wider text-xs uppercase">{cat.name}</span>
                          </button>
                        ))
                      )}
                      <div className="h-px bg-border mx-3 my-1" />
                      <button onClick={() => { setCatDropdownOpen(false); navigate("/products"); }} className="w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-200 flex items-center gap-3">
                        <ShoppingBag size={16} strokeWidth={1.5} className="flex-shrink-0" />
                        <span className="tracking-wider text-xs uppercase">{t("all_products", "All Products")}</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button
                key={link.key}
                onMouseEnter={() => (link as any).isPage && handlePrefetch(link.target)}
                onTouchStart={() => (link as any).isPage && handlePrefetch(link.target)}
                onClick={() => { if ((link as any).isPage) { navigate(`/${link.target}`); } else { scrollToSection(link.target); } }}
                className="px-5 py-2.5 text-[10.5px] font-body font-semibold tracking-[0.25em] uppercase text-foreground/80 hover:text-primary transition-all duration-300 bg-transparent border-none cursor-pointer relative after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-primary after:rounded-full after:transition-all after:duration-300 hover:after:w-2/3"
              >
                {link.label}
              </button>
            );
          })}
        </nav>
      </div>

    </header>

      {/* Mobile Nav — full-screen side overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[55] lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        </div>
      )}
      <div
        ref={mobileMenuRef}
        className={`fixed top-0 left-0 bottom-0 w-[82vw] max-w-[320px] z-[56] lg:hidden transform transition-transform duration-300 ease-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={blackHeaderStyles}
      >
        <div className="h-full bg-background flex flex-col overflow-y-auto">
          {/* Menu header with logo */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/30">
            {logoUrl && (
              <img src={logoUrl} alt="Fatema Farista" className="h-8 w-auto" />
            )}
            <button
              onClick={() => setMobileOpen(false)}
              className="p-1.5 rounded-lg text-foreground/70 hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          {/* Nav links */}
          <div className="flex-1 py-3 px-4">
            {navLinks.map((link, i) => {
              if ((link as any).isDropdown) {
                return (
                  <div key={link.key}>
                    <button
                      onClick={() => setMobileCatOpen(!mobileCatOpen)}
                      className="w-full flex items-center justify-between py-3 px-3 rounded-xl text-[13px] font-body font-semibold tracking-[0.12em] uppercase text-foreground/85 hover:text-primary hover:bg-primary/5 transition-all duration-200"
                    >
                      <span className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <ChevronDown size={14} className="text-primary" />
                        </span>
                        {link.label}
                      </span>
                      <ChevronDown size={13} className={`transition-transform duration-200 text-primary/50 ${mobileCatOpen ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${mobileCatOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"}`}>
                      <div className="ml-11 py-1 flex flex-col gap-0.5">
                        {categories.map((cat) => (
                          <button key={cat.id} onClick={() => handleCategoryClick(cat.slug)} className="text-left py-2 px-3 rounded-lg text-xs font-body tracking-wider text-foreground/65 hover:text-primary hover:bg-primary/5 transition-all duration-200 bg-transparent border-none cursor-pointer flex items-center gap-2">
                            {cat.image && <img src={cat.image} alt={cat.name} className="w-5 h-5 rounded-md object-cover" loading="lazy" />}
                            {cat.name}
                          </button>
                        ))}
                        <button onClick={() => { setMobileCatOpen(false); setMobileOpen(false); navigate("/products"); }} className="text-left py-2 px-3 rounded-lg text-xs font-body tracking-wider text-foreground/65 hover:text-primary hover:bg-primary/5 transition-all duration-200 bg-transparent border-none cursor-pointer flex items-center gap-2">
                          <ShoppingBag size={13} strokeWidth={1.5} />
                          {t("all_products", "All Products")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              const iconMap: Record<string, React.ReactNode> = {
                home: <Home size={14} className="text-primary" />,
                shop: <ShoppingBag size={14} className="text-primary" />,
                "best-selling": <span className="text-primary text-[12px]">★</span>,
                "new-arrivals": <span className="text-primary text-[12px]">✦</span>,
                "all-products": <ShoppingBag size={14} className="text-primary" />,
                reviews: <Heart size={14} className="text-primary" />,
                contact: <span className="text-primary text-[12px]">✉</span>,
              };

              return (
                <button
                  key={link.key}
                  onClick={() => { setMobileOpen(false); if ((link as any).isPage) { navigate(`/${link.target}`); } else { scrollToSection(link.target); } }}
                  className="w-full flex items-center gap-3 py-3 px-3 rounded-xl text-[13px] font-body font-semibold tracking-[0.12em] uppercase text-foreground/85 hover:text-primary hover:bg-primary/5 transition-all duration-200"
                >
                  <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    {iconMap[link.key] || <span className="text-primary text-[12px]">›</span>}
                  </span>
                  {link.label}
                </button>
              );
            })}
          </div>

          {/* Bottom section */}
          <div className="px-5 py-4 border-t border-border/30">
            <button
              onClick={() => { setMobileOpen(false); navigate("/wishlist"); }}
              className="w-full flex items-center gap-3 py-2.5 px-3 rounded-xl text-[12px] font-body font-medium tracking-wider uppercase text-foreground/60 hover:text-primary hover:bg-primary/5 transition-all"
            >
              <Heart size={14} strokeWidth={1.5} />
              Wishlist
            </button>
          </div>
        </div>
      </div>

    {searchOpen && <Suspense fallback={null}><SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} /></Suspense>}
    </>
  );
});

Header.displayName = "Header";

export default Header;
